import type { ReactNode } from "react";

// Gemeinsames Seitengeruest der Tobis-Seiten: Padding, Eyebrow, Titel, Intro.
// Uebernimmt das Muster der bestehenden Seiten (SettingsPage), aber als div und
// nicht als zweites <main> — AppShell rendert das main bereits, verschachtelte
// main-Elemente sind ein Accessibility-Fehler.
//
// Responsive per Container-Varianten (@sm/@lg) nach ADR-0020: das main in
// AppShell traegt @container, eigene verschachtelte Container sind untersagt.
export function TobisPageShell({
  eyebrow,
  title,
  intro,
  action,
  children,
}: {
  eyebrow: string;
  title: string;
  intro?: string;
  /**
   * Aktion zum Gegenstand der Seite, direkt neben der Ueberschrift (aktuell der
   * Favoriten-Stern der Detailseiten). Bewusst hier und nicht frei im Inhalt:
   * eine Aktion, die den ganzen Datensatz betrifft, gehoert an seinen Namen -
   * am rechten Seitenrand stuende sie bei 1600px Breite neben nichts.
   */
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10">
      <div className="mb-2 text-[10px] tracking-[0.25em] text-ink/55">{eyebrow}</div>
      <div className="flex items-baseline gap-3">
        <h1 className="min-w-0 font-serif text-5xl leading-tight">{title}</h1>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      {intro ? <p className="mt-2 max-w-2xl text-sm text-ink/70">{intro}</p> : null}
      <div className="mt-8 space-y-6">{children}</div>
    </div>
  );
}

// Suchfeld. Kein Primitive vorhanden, deshalb inline mit den kanonischen Rungs
// (ring-ink/10 fuer Inputs, ring-ink/30 fuer Fokus).
export function TobisSearch({
  value,
  onChange,
  label,
  placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  label: string;
  placeholder: string;
}) {
  return (
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      placeholder={placeholder}
      // Safari zeigt sonst seine eigene Textvorschlag-Chip-UI bei
      // type="search" (z.B. "leonardo" -> "Leonardo") - das ist macOS/
      // WebKit-Verhalten, kein App-Feature, deshalb explizit abgeschaltet.
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
      className="w-full max-w-sm rounded-md bg-page px-3 py-1.5 text-[13px] outline-none ring-1 ring-ink/10 focus-visible:ring-ink/30"
    />
  );
}
